import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectStop, setSelectedDay, addFacility, setSpecimenTypes, setActingContext } from '../store/stopsSlice';
import { StatusBadge, PriorityBadge, DrawListBanner } from '../components/index';
import { getTodayRoute } from '../services/routeService';
import { addStopsUser, getSpecimenTypes } from '../services/stopsService';
import { formatLocalDate, FULL_DAYS, getScheduledDate, localTimeToUtcString, MONTHS, REFRESH_INTERVAL_MS, utcTimeToLocal } from '../Utils';
import { loaderActions } from '../store/loaderSlice';
import { MdOutlineRefresh } from "react-icons/md";
import { showToast } from '../store/uiSlice';
import { getAllPhlebs, getAllDrivers, getAllFacility } from "../services/userService";
import { mergeDriverStops, selectStop as routeSelectStop } from '../store/routeSlice';

function AddStopModal({ open, onClose, onStopAdded }) {
  const [facility, setFacility] = useState('');
  const [date, setDate] = useState('today');
  const [phlebotomist, setPhlebotomist] = useState();
  const [phlebData, setPhlebData] = useState([]);
  const [driverData, setDriverData] = useState([]);
  const [facilityData, setFacilityData] = useState([]);
  const [courier, setCourier] = useState('');
  const [notes, setNotes] = useState('');
  const [stat, setStat] = useState(false); // CLAUDE-SYNC: v1.7.12 — 'Stat' tag, addable from the field app per Tanaya's spec
  const [timeWindow, setTimeWindow] = useState(new Date().toTimeString().slice(0, 5));
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    getPhlebs();
    getDriver();
    getFacility();

    let interval = setTimeout(() =>  getFacility(), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const getPhlebs = async () => {
    try {
      const res = await getAllPhlebs();
      const phlebs = res?.data || [];

      setPhlebData(phlebs);
      const currentPhleb = phlebs.find((p) => p.id === user?.id || p.email === user?.email);

      if (currentPhleb) {
        setPhlebotomist(currentPhleb.id);
      }
      
    } catch (error) {
      console.log(error);
    }
  };

  const getDriver = async () => {
    try {
      const res = await getAllDrivers();
      setDriverData(res?.data || []);
    } catch (error) {
      console.log(error);
    }
  };

  const getFacility = async () => {
    try {
      const res = await getAllFacility();
      const facilities = res?.data || [];
      const sorted = facilities.filter((f) => f.status === true).sort((a, b) => a.name.localeCompare(b.name));
      setFacilityData(sorted);
    } catch (error) {
      console.log(error);
    }
  };

  const handleSave = async () => {
    const scheduledDate = getScheduledDate(date);

    const payload = {
      facilityId: facility,
      date: scheduledDate,
      timeWindow: localTimeToUtcString(timeWindow, scheduledDate),
      phlebId: phlebotomist || null,
      driverId: courier || null,
      notes: notes || null,
      priority: stat, // CLAUDE-SYNC: v1.7.12 — 'Stat' tag, field name stays 'priority' server-side
    };

    try {
      const res = await addStopsUser(payload);
      onStopAdded?.();
      onClose();
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div className={`detail-panel ${open ? 'open' : ''}`} style={{ zIndex: 50 }}>
      {/* Header */}
      <div style={{
        background: 'var(--rp)', padding: '16px 20px',
        display: 'flex', alignItems: 'center', gap: 14,
      }}>
        <button className="back-btn" onClick={onClose}>← Back</button>
        <div>
          <div style={{ color: 'white', fontSize: 17, fontFamily: "'Lora', serif" }}>Add a Stop</div>
          <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11, marginTop: 2 }}>Assign for today or a future date</div>
        </div>
      </div>

      {/* Form */}
      <div style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ background: 'white', borderRadius: 16, padding: '18px 16px', boxShadow: '0 1px 6px rgba(0,0,0,0.07)' }}>
          {/* Facility */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--rp)', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 6 }}>Facility</div>
            <select
              value={facility}
              onChange={e => setFacility(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px',
                border: '1px solid var(--rgray)', borderRadius: 10,
                fontFamily: 'inherit', fontSize: 13, outline: 'none',
                background: 'white', color: facility ? '#1a1a1a' : '#8A7A8D',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='%238A7A8D'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
              }}
            >
              <option value="">Select a facility…</option>
              {facilityData.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>

          {/* Date */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--rp)', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 6 }}>Date</div>
            <select
              value={date}
              onChange={e => setDate(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px',
                border: '1px solid var(--rgray)', borderRadius: 10,
                fontFamily: 'inherit', fontSize: 13, outline: 'none',
                background: 'white',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='%238A7A8D'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
              }}
            >
              <option value="today">Today — {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</option>
              <option value="tomorrow">Tomorrow</option>
              <option value="day2">In 2 days</option>
              <option value="day3">In 3 days</option>
            </select>
          </div>

          <div style={{  display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--rp)', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 6 }}>Time Window</div>
            <input
              style={
                {
                  border: '1px solid var(--rgray)',
                  borderRadius: 10,
                  padding: '10px 12px',
                  outline: 'none',
                }
              }
              type="time"
              value={timeWindow} 
              onChange={e => setTimeWindow(e.target.value)} />
          </div>


          {/* Assign Phlebotomist */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--rp)', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 6 }}>Assign Phlebotomist</div>
            <select
              value={phlebotomist || ""}
              onChange={(e) => setPhlebotomist(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px',
                border: '1px solid var(--rgray)', borderRadius: 10,
                fontFamily: 'inherit', fontSize: 13, outline: 'none',
                background: 'white', color: phlebotomist ? '#1a1a1a' : '#8A7A8D',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='%238A7A8D'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
              }}
            >
              {phlebData.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              <option value="">Select or leave unassigned…</option>
            </select>
          </div>

          {/* Assign Courier */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--rp)', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 6 }}>
              Assign Courier <span style={{ fontWeight: 400, textTransform: 'none', color: '#8A7A8D', letterSpacing: 0 }}>(optional)</span>
            </div>
            <select
              value={courier}
              onChange={e => setCourier(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px',
                border: '1px solid var(--rgray)', borderRadius: 10,
                fontFamily: 'inherit', fontSize: 13, outline: 'none',
                background: 'white', color: courier ? '#1a1a1a' : '#8A7A8D',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='%238A7A8D'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
              }}
            >
              <option value="">Select or leave TBD…</option>

              {driverData.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          {/* Stat */}
          {/* CLAUDE-SYNC: v1.7.12 — Stat tag, addable from the field app.
              Previously back-office only; leads need to flag a stop as a
              stat draw when a site calls mid-day, from wherever they're
              adding the stop. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox" id="statCb"
              checked={stat}
              onChange={e => setStat(e.target.checked)}
              style={{ width: 16, height: 16 }}
            />
            <label htmlFor="statCb" style={{ fontSize: 13, cursor: 'pointer' }}>Stat</label>
          </div>

          {/* Notes */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--rp)', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 6 }}>
              Notes for Field Staff <span style={{ fontWeight: 400, textTransform: 'none', color: '#8A7A8D', letterSpacing: 0 }}>(optional)</span>
            </div>
            <textarea
              rows={3}
              placeholder="e.g. use side entrance, ask for Nurse Johnson…"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px',
                border: '1px solid var(--rgray)', borderRadius: 10,
                fontFamily: 'inherit', fontSize: 13, outline: 'none',
                resize: 'none', boxSizing: 'border-box',
              }}
            />
          </div>

          <button
            onClick={handleSave}
            disabled={!facility}
            style={{
              width: '100%', padding: 14, borderRadius: 30, marginTop: 4,
              background: facility ? 'var(--rp)' : '#E8E4EA',
              color: facility ? 'white' : '#8A7A8D',
              border: 'none', fontFamily: 'Poppins, sans-serif',
              fontSize: 14, fontWeight: 600,
              cursor: facility ? 'pointer' : 'default',
            }}
          >
            Save Stop
          </button>
        </div>
      </div>
    </div>
  );
}

function PhlebView({ isActive }) {
  const [spinner, setSpinner] = useState(false);
  const [showAddStop, setShowAddStop] = useState(false);
  const dispatch = useDispatch();
  const { facilities, stopStates, drawListPhotos, selectedDay } = useSelector((s) => s.stops);
  const { user } = useSelector((s) => s.auth);   // ← naya
  const isToday = ""
  const timerRef = useRef(null);
  
   const getActingRole = (stop) => {
    if (stop.driverId === user?.id) return 'driver';
    if (stop.phlebId === user?.id) return 'phleb';
    return 'phleb'; // fallback — purane behavior jaisa
  };

  function getDayLabel(offset) {
    const base = new Date();
    base.setDate(base.getDate() + parseInt(offset));
    return `${FULL_DAYS[base.getDay()]}, ${MONTHS[base.getMonth()]} ${base.getDate()}`;
  }

  useEffect(() => {
    getSpecimenTypes()
      .then((res) => {
        const types = res?.data ?? [];
        if (types.length) dispatch(setSpecimenTypes(types));
      })
      .catch((err) => console.warn('Could not load specimen types:', err));
  }, []);


  useEffect(() => {
    fetchRoutes();
    if (!isActive) return;
    const interval = setInterval(() => fetchRoutes(true), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [selectedDay, isActive]);

  const fetchRoutes = async (isPolling = false) => {
    try {
      const base = new Date();
      base.setDate(base.getDate() + parseInt(selectedDay));
      const formattedDate = formatLocalDate(base);

      if (isPolling) {
        dispatch(loaderActions.fetched());
      }

      const res = await getTodayRoute(formattedDate);
      dispatch(addFacility(res?.data));
      
      const routes = Array.isArray(res?.data) ? res.data : res?.data ? [res.data] : [];
      const allIncomingStops = routes.flatMap((r) => r.stops || []);
      const driverAssignedStops = allIncomingStops.filter((s) => s.driverId === user?.id);
      if (driverAssignedStops.length) {
        dispatch(mergeDriverStops(driverAssignedStops));
      }
    } catch (err) {
      console.error('Route fetch failed:', err);
    }
  };

  const handleRefresh = () => {
    dispatch(showToast("Page Refresh"))
    fetchRoutes(false);
    setSpinner(true);

    setInterval(() => {
      setSpinner(false);
    }, 1800);
  }

  // FIX: dedupe stops before rendering.
  // A stop can end up in `facilities` twice — once via addFacility() (phleb
  // route) and once via mergeDriverStops() (driver route). If the same user
  // is both the phleb AND the driver on a stop (e.g. after self-delivering
  // "to lab", which reassigns driverId to the phleb's own id), that stop
  // renders as two separate cards. Flatten + dedupe by stop.id before
  // mapping to JSX, regardless of which slice/reducer it came from.
  const allStopsFlat = facilities.flatMap((f) => f.stops || []);
  const seenStopIds = new Set();
  const uniqueStops = allStopsFlat.filter((stop) => {
    if (seenStopIds.has(stop.id)) return false;
    seenStopIds.add(stop.id);
    return true;
  });

  // FIX: courier/driver-assigned stops should render last, phleb's own
  // stops first. Array.sort is stable, so relative order within each
  // group (phleb stops among themselves, driver stops among themselves)
  // is preserved — only the two groups get reordered.
  const sortedStops = [...uniqueStops].sort((a, b) => {
    const aIsDriver = getActingRole(a) === 'driver' ? 1 : 0;
    const bIsDriver = getActingRole(b) === 'driver' ? 1 : 0;
    return aIsDriver - bIsDriver;
  });

  return (
    <div className="view active" style={{ paddingBottom: 80, marginTop: "8vh" }}>

      <div className="date-bar">
        <div className="date-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          Viewing — <strong>{getDayLabel(selectedDay)}</strong>
          {(
            <span
              title="Auto-refreshing every 20 s"
              style={{
                display: 'inline-block', width: 7, height: 7,
                borderRadius: '50%', background: '#27AE60',
                animation: 'pulse 2s infinite',
                flexShrink: 0,
              }}
            />
          )}
        </div>

        <select
          value={selectedDay}
          onChange={(e) => dispatch(setSelectedDay(e.target.value))}
          style={{
            padding: '4px 10px', border: '1px solid var(--rgray)', borderRadius: 8,
            fontFamily: "'Poppins', sans-serif", fontSize: 12, color: 'var(--rp)',
            fontWeight: 600, outline: 'none', background: 'white', cursor: 'pointer',
          }}
        >
          {[5, 4, 3, 2, 1, 0, -1, -2, -3, -4, -5, -6, -7].map((offset) => {
            const label =
              offset === 0 ? `Today — ${getDayLabel(0)}` :
                offset === -1 ? `Yesterday — ${getDayLabel(-1)}` :
                  offset === 1 ? `Tomorrow — ${getDayLabel(1)}` :
                    offset > 1 ? `In ${offset} days — ${getDayLabel(offset)}` :
                      getDayLabel(offset);
            return (
              <option key={offset} value={String(offset)}>
                {label}
              </option>
            );
          })}
        </select>
        <MdOutlineRefresh
          className={spinner ? 'spin-refresh' : ""}
          size={25} onClick={() => handleRefresh()}
          title='Refresh'
          style={{ cursor: "pointer" }}
        />
      </div>

      <div className="section-title">My Stops Today</div>

      {/* Stop list */}
      {sortedStops.length > 0 ? (
        <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 10, marginTop: '1rem' }}>
          {sortedStops.map((stop, index) => {
            const actingRole = getActingRole(stop);   // ← naya
            const s = stopStates[stop.id] || 0;
            const photos = stop.photos ?? [];
            const note = stop.notes ?? '';
            const sumCounts = (v) =>
              Array.isArray(v)
                ? v.reduce((s, c) => s + Number(c.count ?? c.collected ?? 0), 0)
                : Object.values(v || {}).reduce((s, n) => s + (Number(n) || 0), 0);

            const totalSpecimen = sumCounts(stop.specimenCounts);
            const totalCollected = sumCounts(stop.collectedCounts);
            const isPhlebOnly = stop.serviceType === "phlebotomy_only";

            return (
              <div
                key={stop.id}
                className={`stop-card ${stop.priority ? 'priority' : ''}`}
                onClick={() => {
                  if (actingRole === 'driver') {
                    // driver-role: route slice ka apna selectStop use karo
                    // (yahan import name clash na ho isliye routeSelectStop alias use karo — neeche import note dekho)
                    dispatch(routeSelectStop(stop.id));
                    dispatch(setActingContext({ stopId: stop.id, role: 'driver' }));
                  } else {
                    dispatch(selectStop({ id: stop.id, actingRole: 'phleb' }));
                  }
                }}
              >
                <div className="stop-card-top">
                  <div className="stop-num" style={stop.priority ? { background: '#E65100' } : {}}>
                    {index + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div className="stop-name">{stop.facilityName}</div>
                      {stop.priority && <PriorityBadge />}
                    </div>
                    <div className="stop-addr">{stop.addr}</div>
                    {/* NAYA: courier-duty badge */}
                    {actingRole === 'driver' && (
                      <div style={{ fontSize: 11, color: '#1565C0', fontWeight: 600, marginTop: 2 }}>
                        🚐 Courier Pickup — assigned to you
                      </div>
                    )}
                  </div>
                  <StatusBadge status={s} isPhlebOnly={isPhlebOnly} />
                </div>

                <div className="stop-meta">
                  <div className="stop-meta-item">
                    <div className="dot" style={{ background: 'var(--rp)' }} />
                    Est. {utcTimeToLocal(stop.timeWindow)}
                  </div>
                  <div className="stop-meta-item">
                    <div className="dot" style={{ background: 'var(--berry)' }} />
                    {totalSpecimen} samples
                  </div>
                  <div className="stop-meta-item">
                    <div className="dot" style={{ background: 'var(--berry)' }} />
                    {totalCollected} collected
                  </div>
                  <div className="stop-meta-item">
                    <div className="dot" style={{ background: '#27AE60' }} />
                    {photos.length} photo{photos.length !== 1 ? 's' : ''}
                  </div>
                </div>

                {note ? (
                  <div className="note-preview">
                    <span style={{ fontSize: 12 }}>✎</span>
                    {note}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--bodygray)', fontSize: 13 }}>
          <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.4 }}>📋</div>
          No stops scheduled for {getDayLabel(selectedDay)}
        </div>
      )}
      
      {/* + Add a Stop button */}
      <div style={{ padding: '12px 16px 8px' }}>
        <button
          onClick={() => setShowAddStop(true)}
          style={{
            width: '100%', padding: '14px 0',
            border: '2px dashed var(--berry)', borderRadius: 14,
            background: 'transparent', color: 'var(--berry)',
            fontFamily: "'Poppins', sans-serif", fontSize: 14, fontWeight: 600,
            cursor: 'pointer', letterSpacing: '0.01em',
          }}
        >
          + Add a Stop
        </button>
      </div>

      <AddStopModal 
        open={showAddStop}
        onClose={() => setShowAddStop(false)}
        onStopAdded={() => fetchRoutes(true)}
      />

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}

export default PhlebView;